import { GetStaticProps, NextPage } from 'next'
import { Stack, Heading } from '@instabox/consumer-web-ui'
import {
  PageProps,
  localeToMarketCode,
  fetchMarketById,
  fetchPage,
  fetchPageLinks,
  getTranslation,
  fetchBanners,
} from '@instabox/consumer-web-shared'
import { HTMLMotionProps } from 'framer-motion'

import PageContent from '../../components/PageContent'
import PageSection from '../../components/PageSection'
import InViewAnimation from '../../components/InViewAnimation'

export interface AboutPageProps extends PageProps {}

const SLUG = 'about'

const AboutPage: NextPage<AboutPageProps> = ({ content }) => {
  const [lockerImage, flagImage] = content.filter(({ item }) => item.__typename === 'images')
  const [firstTitle, secondTitle] = content.filter(({ item }) => item.__typename === 'titles')

  return (
    <>
      <PageContent fields={['heroes']} content={content}>
        <Stack gap="lg">
          <div>
            <InViewAnimation {...heroContentAnimation(0.2)}>
              <Heading fontSize="xxl" color="white" align="center" spacing="xs">
                {/* @ts-ignore */}
                {getTranslation('title', firstTitle?.item?.translations, firstTitle?.item?.title)}
              </Heading>
            </InViewAnimation>
            <InViewAnimation {...heroContentAnimation(0.4)}>
              <Heading fontSize="xxl" color="white" align="center" spacing="xs">
                {/* @ts-ignore */}
                {getTranslation('title', secondTitle?.item?.translations, secondTitle?.item?.title)}
              </Heading>
            </InViewAnimation>
          </div>
          <InViewAnimation {...heroContentAnimation(0.6)}>
            <PageContent content={content} fields={['subtitles']} slug={SLUG} />
          </InViewAnimation>
        </Stack>
      </PageContent>
      <PageContent
        content={content}
        fields={['texts_with_images']}
        slug={SLUG}
        textsWithImagesLayout="fullBleed"
      />
      <PageSection backgroundColor="var(--color-silly-salmon)">
        <div style={{ marginBottom: 'calc(var(--gap-xl) * -2)', overflowY: 'hidden' }}>
          <InViewAnimation {...lockerImageAnimation} threshold={0.1}>
            <PageContent content={[lockerImage]} fields={['images']} slug={SLUG} />
          </InViewAnimation>
        </div>
      </PageSection>
      <PageContent content={[flagImage]} fields={['images']} slug={SLUG} />
    </>
  )
}

export default AboutPage

export const getStaticProps: GetStaticProps<AboutPageProps> = async ({ locale }) => {
  const {
    data: { marketById },
  } = await fetchMarketById(localeToMarketCode(locale))
  const page = await fetchPage(locale, SLUG)
  const pageLinks = await fetchPageLinks(locale)
  const banners = await fetchBanners(locale, SLUG)

  return {
    props: {
      market: marketById,
      pageLinks,
      pageContainerVariant: 'secondary',
      fullBleed: true,
      banners,
      ...page,
    },
    notFound: !page,
    revalidate: 60,
  }
}

const heroContentAnimation = (delay: number): HTMLMotionProps<'div'> => {
  const y = -100 * delay
  return {
    variants: {
      visible: { opacity: 1, y: '0' },
      hidden: { opacity: 0, y: `${y}px` },
    },
    transition: {
      duration: 0.7,
      ease: 'easeOut',
      delay,
    },
  }
}

const lockerImageAnimation: HTMLMotionProps<'div'> = {
  variants: {
    visible: { opacity: 1, transform: 'scale(1)' },
    hidden: { opacity: 0, transform: 'scale(2)' },
  },
  transition: {
    duration: 0.5,
  },
}
